import { getLanguageForPath as getLanguageForPathFromUi } from "@promptlayer/ui/skills/skill-file-support";

import type { PresetFile } from "../skill-presets";

const URL_SCHEME_PATTERN = /^[a-z][a-z\d+\-.]*:/i;

export const normalizeTreeItemName = (name: string): string => name.trim();

export const getTreeItemNameError = (name: string, itemType: "file" | "folder"): string | null => {
  const normalizedName = normalizeTreeItemName(name);

  if (!normalizedName) {
    return itemType === "folder" ? "Folder name is required." : "File name is required.";
  }

  if (normalizedName.includes("/") || normalizedName.includes("\\")) {
    return "Name cannot contain / or \\.";
  }

  if (normalizedName === ".keep.md") {
    return '".keep.md" is reserved.';
  }

  return null;
};

export const normalizeRelativePath = (path: string) =>
  path.replace(/^\/+/, "").replace(/\/+/g, "/").replace(/\/$/, "");

export const stripRootPathPrefix = (path: string, rootPath?: string | null) => {
  const normalizedPath = normalizeRelativePath(path);
  const normalizedRoot = normalizeRelativePath(rootPath ?? "");

  if (!normalizedRoot) {
    return normalizedPath;
  }

  if (normalizedPath === normalizedRoot) {
    return "";
  }

  if (normalizedPath.startsWith(`${normalizedRoot}/`)) {
    return normalizedPath.slice(normalizedRoot.length + 1);
  }

  return normalizedPath;
};

export const getParentPath = (path: string) => {
  const parts = path.split("/").filter(Boolean);
  return parts.length > 1 ? parts.slice(0, -1).join("/") : "";
};

export type SkillPathChangeReviewKind = "renamed" | "moved";

export const getPathChangeReviewKind = (
  oldPath: string,
  newPath: string,
): SkillPathChangeReviewKind => {
  if (getParentPath(oldPath) === getParentPath(newPath)) {
    return "renamed";
  }
  return "moved";
};

export const resolveSkillCollectionLinkPath = (href: string, currentOrigin?: string) => {
  const trimmedHref = href.trim();
  if (!trimmedHref || trimmedHref.startsWith("#") || trimmedHref.startsWith("?")) {
    return null;
  }

  if (currentOrigin) {
    try {
      const parsedUrl = new URL(trimmedHref, currentOrigin);
      if (parsedUrl.origin === currentOrigin && parsedUrl.pathname && parsedUrl.pathname !== "/") {
        return normalizeRelativePath(decodeURIComponent(parsedUrl.pathname));
      }
    } catch {
      return null;
    }
  }

  if (URL_SCHEME_PATTERN.test(trimmedHref)) {
    return null;
  }

  const [pathOnly] = trimmedHref.split(/[?#]/);
  if (!pathOnly) {
    return null;
  }

  return pathOnly.startsWith("/") ? normalizeRelativePath(pathOnly) : null;
};

export const buildAbsoluteSkillCollectionHref = (targetFilePath: string) =>
  `/${normalizeRelativePath(targetFilePath)}`;

export const buildPathWithinParent = (parentPath: string | null, name: string) => {
  const normalizedName = normalizeTreeItemName(name);
  return parentPath ? normalizeRelativePath(`${parentPath}/${normalizedName}`) : normalizedName;
};

export const getPathDepth = (path: string) => path.split("/").filter(Boolean).length;

export const getItemName = (path: string) => path.split("/").filter(Boolean).pop() ?? path;

export const formatSkillRelativePathForDisplay = (relativePath: string): string => {
  if (!relativePath) {
    return "";
  }

  const parts = relativePath.split("/").filter(Boolean);
  if (parts.length === 0) {
    return "";
  }

  return parts.join(" › ");
};

export const getLanguageForPath = (path: string): string => getLanguageForPathFromUi(path);

export const isOpenaiYamlFilePath = (path: string): boolean =>
  path.split("/").pop()?.toLowerCase() === "openai.yaml";

export const isMarkdownSkillFilePath = (path: string): boolean =>
  path.toLowerCase().endsWith(".md");

export const normalizePresetFiles = (presetFiles: PresetFile[], rootPath?: string | null) =>
  presetFiles.map(file => ({
    ...file,
    path: stripRootPathPrefix(file.path, rootPath),
  }));
